import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { signInUser, signOutUser, updateAccessToken } from 'redux/slices/user.slice';
import { User } from 'redux/types/user';
import baseApi from 'services/api/baseApi';
import { adopted } from 'services/session/sessionMiddleware';
import { isSwitchPending, markSwitchPending, readSession, subscribeToSession } from 'services/session/sessionStore';
import { reloadWindow } from 'utils/windowNavigation';

interface UseSessionSyncReturn {
  pendingSwitch: boolean;
  confirmSwitch: () => void;
}

// Keeps this tab in step with what other tabs do to the shared session:
// - logout elsewhere → sign out now (no waiting for a 401)
// - login elsewhere → a tab sitting on /sign-in gets in
// - token refreshed elsewhere, same project → adopt it, no request
// - switch to another project → prompt; the user reloads when ready. Reloading every tab at once
//   floods the API, and silently adopting would let the user keep editing another project's screen.
const useSessionSync = (currentUser: User | null): UseSessionSyncReturn => {
  const dispatch = useDispatch();
  const [pendingSwitch, setPendingSwitch] = useState(false);
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  useEffect(() => {
    // The prompt outlives a remount of this hook (route change).
    if (isSwitchPending()) setPendingSwitch(true);

    const prompt = (): void => {
      if (isSwitchPending()) return;
      markSwitchPending();
      setPendingSwitch(true);
    };

    const adopt = (session: User | null): void => {
      const current = currentUserRef.current;

      if (session === null) {
        if (!current?.accessToken) return;
        dispatch(baseApi.util.resetApiState());
        dispatch(adopted(signOutUser()));
        return;
      }

      if (!current?.accessToken) {
        dispatch(baseApi.util.resetApiState());
        dispatch(adopted(signInUser(session)));
        return;
      }

      if ((session.projectId ?? null) !== (current.projectId ?? null)) {
        prompt();
        return;
      }

      if (session.accessToken !== current.accessToken) dispatch(adopted(updateAccessToken(session.accessToken)));
    };

    // Frozen / bfcache tabs can miss the storage event: re-check the shared session on wake-up.
    const catchUp = (): void => {
      const session = readSession();
      const current = currentUserRef.current;
      if (session && current?.accessToken && (session.projectId ?? null) !== (current.projectId ?? null)) prompt();
    };
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') catchUp();
    };

    const unsubscribe = subscribeToSession(adopt);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', catchUp);

    return (): void => {
      unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', catchUp);
    };
  }, [dispatch]);

  // Reloading boots from the shared session (token + projectId together): no half-applied state.
  const confirmSwitch = (): void => reloadWindow();

  return { pendingSwitch, confirmSwitch };
};

export default useSessionSync;
